import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  PictureAsPdf as PDFIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../store';
import {
  fetchProformas,
  createProforma,
  updateProforma,
  deleteProforma,
  updateProformaStatusAction,
  searchProformas,
} from '../store/slices/billingSlice';
import { Proforma, ProformaCreateInput, ProformaUpdateInput } from '../services/billingService';
import { useConfirm } from '../contexts/ConfirmDialogContext';

interface ProformaQuotationProps {
  caseId: number;
  clientId: number;
  billingType: string;
  onProformaCreated?: (proforma: Proforma) => void;
}

const ProformaQuotation: React.FC<ProformaQuotationProps> = ({
  caseId,
  clientId,
  billingType,
  onProformaCreated,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const confirm = useConfirm();
  const { proformas, loading, error } = useSelector((state: RootState) => state.proformas);
  const { user } = useSelector((state: RootState) => state.auth);

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProforma, setSelectedProforma] = useState<Proforma | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [searchQuery, setSearchQuery] = useState('');

  const [formData, setFormData] = useState<ProformaCreateInput>({
    case_id: caseId,
    customer_id: clientId,
    issued_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    currency: 'USD',
    subtotal: 0,
    tax_rate: 0,
    tax_amount: 0,
    discount: 0,
    total: 0,
    notes: '',
    items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
  });

  useEffect(() => {
    if (user?.user_id) {
      dispatch(fetchProformas(user.user_id.toString()));
    }
  }, [dispatch, user]);

  const handleSearch = () => {
    if (user?.user_id && searchQuery.trim()) {
      dispatch(searchProformas({ userId: user.user_id.toString(), query: searchQuery }));
    } else if (user?.user_id) {
      dispatch(fetchProformas(user.user_id.toString()));
    }
  };

  const handleCreateProforma = async () => {
    try {
      const result = await dispatch(createProforma(formData));
      if ('error' in result) {
        setSnackbar({ open: true, message: t('billing.failedToCreateProforma'), severity: 'error' });
      } else {
        setSnackbar({ open: true, message: t('billing.proformaCreated'), severity: 'success' });
        setOpenDialog(false);
        resetForm();
        if (onProformaCreated) {
          onProformaCreated(result.payload as Proforma);
        }
      }
    } catch (error) {
      setSnackbar({ open: true, message: t('billing.failedToCreateProforma'), severity: 'error' });
    }
  };

  const handleUpdateProforma = async () => {
    if (!selectedProforma) return;
    
    const updateData: ProformaUpdateInput = {
      id: selectedProforma.id,
      ...formData,
    };
    
    try {
      const result = await dispatch(updateProforma(updateData));
      if ('error' in result) {
        setSnackbar({ open: true, message: t('billing.failedToUpdateProforma'), severity: 'error' });
      } else {
        setSnackbar({ open: true, message: t('billing.proformaUpdated'), severity: 'success' });
        setOpenDialog(false);
        setSelectedProforma(null);
        resetForm();
      }
    } catch (error) {
      setSnackbar({ open: true, message: t('billing.failedToUpdateProforma'), severity: 'error' });
    }
  };

  const handleDeleteProforma = async (proformaId: number) => {
    if (!user?.user_id) return;

    const ok = await confirm(t('billing.confirmDeleteProforma'));
    if (ok) {
      try {
        const result = await dispatch(deleteProforma({ proformaId, userId: user.user_id.toString() }));
        if ('error' in result) {
          setSnackbar({ open: true, message: t('billing.failedToDeleteProforma'), severity: 'error' });
        } else {
          setSnackbar({ open: true, message: t('billing.proformaDeleted'), severity: 'success' });
        }
      } catch (error) {
        setSnackbar({ open: true, message: t('billing.failedToDeleteProforma'), severity: 'error' });
      }
    }
  };

  const handleUpdateStatus = async (proformaId: number, status: string) => {
    if (!user?.user_id) return;
    
    try {
      const result = await dispatch(updateProformaStatusAction({ 
        proformaId, 
        status, 
        userId: user.user_id.toString() 
      }));
      if ('error' in result) {
        setSnackbar({ open: true, message: t('billing.failedToUpdateProformaStatus'), severity: 'error' });
      } else {
        setSnackbar({ open: true, message: t('billing.proformaStatusUpdated'), severity: 'success' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: t('billing.failedToUpdateProformaStatus'), severity: 'error' });
    }
  };

  const resetForm = () => {
    setFormData({
      case_id: caseId,
      customer_id: clientId,
      issued_date: new Date().toISOString().split('T')[0],
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      currency: 'USD',
      subtotal: 0,
      tax_rate: 0,
      tax_amount: 0,
      discount: 0,
      total: 0,
      notes: '',
      items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
    });
  };

  const openEditDialog = (proforma: Proforma) => {
    setSelectedProforma(proforma);
    setFormData({
      case_id: proforma.case_id,
      customer_id: proforma.customer_id,
      issued_date: proforma.issued_date,
      due_date: proforma.due_date,
      currency: proforma.currency,
      subtotal: proforma.subtotal,
      tax_rate: proforma.tax_rate,
      tax_amount: proforma.tax_amount,
      discount: proforma.discount,
      total: proforma.total,
      notes: proforma.notes || '',
      items: proforma.items,
    });
    setOpenDialog(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'default';
      case 'sent': return 'warning';
      case 'accepted': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <Typography>{t('billing.loadingProformas')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5">{t('cases.proformaQuotations')}</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                size="small"
                placeholder={t('billing.searchProformas')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Button
                variant="outlined"
                onClick={handleSearch}
              >
                {t('common.search')}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedProforma(null);
                  resetForm();
                  setOpenDialog(true);
                }}
              >
                {t('billing.newProforma')}
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('billing.proformaNumber')}</TableCell>
                  <TableCell>{t('billing.client')}</TableCell>
                  <TableCell>{t('billing.issueDate')}</TableCell>
                  <TableCell>{t('billing.dueDate')}</TableCell>
                  <TableCell>{t('common.total')}</TableCell>
                  <TableCell>{t('billing.status')}</TableCell>
                  <TableCell>{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {proformas.map((proforma: Proforma) => (
                  <TableRow key={proforma.id}>
                    <TableCell>{proforma.proforma_number}</TableCell>
                    <TableCell>{t('billing.clientWithId', { id: proforma.customer_id })}</TableCell>
                    <TableCell>{new Date(proforma.issued_date).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(proforma.due_date).toLocaleDateString()}</TableCell>
                    <TableCell>{proforma.currency} {proforma.total.toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={proforma.status}
                        size="small"
                        color={getStatusColor(proforma.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={t('common.edit')}>
                          <IconButton size="small" onClick={() => openEditDialog(proforma)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('billing.send')}>
                          <IconButton
                            size="small"
                            onClick={() => handleUpdateStatus(proforma.id, 'sent')}
                            disabled={proforma.status === 'sent'}
                          >
                            <SendIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.delete')}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteProforma(proforma.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Proforma Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedProforma ? t('billing.editProforma') : t('billing.createProforma')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={t('billing.issueDate')}
              type="date"
              value={formData.issued_date}
              onChange={(e) => setFormData({ ...formData, issued_date: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('billing.dueDate')}
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('billing.totalAmount')}
              type="number"
              value={formData.total}
              onChange={(e) => setFormData({ ...formData, total: parseFloat(e.target.value) })}
              fullWidth
            />
            <TextField
              select
              label={t('cases.currency')}
              value={formData.currency}
              onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
              fullWidth
            >
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
              <MenuItem value="GBP">GBP</MenuItem>
            </TextField>
            <TextField
              label={t('common.notes')}
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={selectedProforma ? handleUpdateProforma : handleCreateProforma}
            variant="contained"
          >
            {selectedProforma ? t('common.update') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProformaQuotation;
